/**
 * The ONE builder for the `/process?engine=v2` run payload.
 *
 * Extracted verbatim from RSAAnalysis.js `handleSubmit` (2026-09-07) so the
 * Run button, its "Queue run" default (`POST /rsa/v2/queue`) and the
 * multi-project Run-queue panel all post byte-for-byte the same body. Pure:
 * every network-derived input (project files, RSA_* folders, deliverables
 * folder, source storage) is resolved by the caller and passed in.
 *
 * Returns `{ ok: true, payload }` or `{ ok: false, code, message, ... }` — the
 * caller maps `code` to whichever alert/notification it used before.
 */
import {
  TASK_OPTION_TO_SERVICE,
  TASK_OPTION_TO_FOLDER_CODE,
  RSA_FOLDER_CODE_TO_SUFFIX,
  UNBILLED_SERVICES,
} from "./rsaTaskCatalog";

export const DEFAULT_TRIGGER_SOURCE = "toggle-btn-rsa-run";

/** Task labels → non-empty template ids (order preserved). */
export const templateIdsForTasks = (taskNames, analysisTemplateIdMapping) =>
  (taskNames || [])
    .map((taskName) => (analysisTemplateIdMapping || {})[taskName])
    .filter((id) => id && id !== "");

/**
 * engine=v2 `analysis_type`: the 3-letter code in parens of the first task the
 * user clicked ("Stopping Sight Distance (SSD)" → "SSD"). Uses the RAW button
 * state, not the route-filtered list, so the type does not depend on whether
 * routes were picked for it yet.
 */
export const deriveAnalysisType = (taskNames) => {
  for (const name of taskNames || []) {
    const match = String(name).match(/\(([A-Z]{3})\)\s*$/);
    if (match) return match[1];
  }
  return null;
};

const safeMetricsMap = (value) =>
  value && typeof value === "object" ? value : {};

const sumMetricValues = (obj) =>
  Object.values(obj || {}).reduce((sum, val) => {
    const num = Number(val);
    return Number.isFinite(num) ? sum + num : sum;
  }, 0);

const normalizeId = (value) => {
  const id = String(value ?? "").trim();
  if (!id) return "";
  const lower = id.toLowerCase();
  if (
    lower === "0" ||
    lower === "null" ||
    lower === "none" ||
    lower === "undefined"
  ) {
    return "";
  }
  return id;
};

/**
 * Routes flow only: align analysis_template_ids with path_folders. Both arrays
 * are rebuilt from the same ordered task list, then sorted by config_type.
 */
export const alignTemplatesToPathFolders = ({
  selectedTaskNames,
  analysisTemplateIdMapping,
  pathFolders,
}) => {
  const pathFolderByConfigType = new Map(
    (pathFolders || [])
      .map((item) => {
        const configType = String(item?.config_type ?? "")
          .trim()
          .toUpperCase();
        const pathFolderId = String(item?.path_folder_id ?? "").trim();
        if (!configType || !pathFolderId) return null;
        return [configType, { config_type: configType, path_folder_id: pathFolderId }];
      })
      .filter(Boolean),
  );

  const seenConfigTypes = new Set();
  const alignedRows = [];
  const missingConfigTypes = [];

  for (const taskName of selectedTaskNames || []) {
    const templateId = String(
      (analysisTemplateIdMapping || {})[taskName] ?? "",
    ).trim();
    if (!templateId) continue;
    const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskName];
    const configType = String(RSA_FOLDER_CODE_TO_SUFFIX[folderCode] ?? "")
      .trim()
      .toUpperCase();
    if (!configType || seenConfigTypes.has(configType)) continue;
    seenConfigTypes.add(configType);

    const matchedPathFolder = pathFolderByConfigType.get(configType);
    if (!matchedPathFolder) {
      missingConfigTypes.push(configType);
      continue;
    }
    alignedRows.push({
      template_id: templateId,
      config_type: matchedPathFolder.config_type,
      path_folder_id: matchedPathFolder.path_folder_id,
    });
  }

  const ordered = [...alignedRows].sort((a, b) =>
    a.config_type.localeCompare(b.config_type),
  );
  return {
    missingConfigTypes,
    analysisTemplateIds: ordered.map((row) => row.template_id),
    pathFolders: ordered.map((row) => ({
      config_type: row.config_type,
      path_folder_id: row.path_folder_id,
    })),
  };
};

/**
 * @param {object} input
 * @param {string|number} input.projectId
 * @param {string} input.projectName            resolved display name ("Project <id>" fallback applied by caller)
 * @param {"rsa-model"|"rsa-existing"} input.projectType
 * @param {string} input.userEmail
 * @param {string[]} input.selectedTasks         raw task-button state (analysis_type derivation)
 * @param {string[]} input.selectedTaskNames     tasks actually submitted (route-filtered)
 * @param {object} input.analysisTemplateIdMapping
 * @param {boolean} input.hasRoutes              routes flow (path_folders) vs folder flow (path_data)
 * @param {Array}  input.pathFolders             [{config_type, path_folder_id}] from the project's RSA_* folders
 * @param {string[]} input.modelFolderIds
 * @param {string} input.outputFolderId          Deliverables folder Drive id (required)
 * @param {"drive"|"s3"} input.sourceStorage
 * @param {string} input.sourceFolderId          existing Source folder Drive id (non-model, Drive projects)
 * @param {string[]} input.selectedFolderIds     folder flow: folders ticked in the tree
 * @param {string} input.wkt
 * @param {object|null} input.horizontalData
 * @param {object|null} input.embankmentData
 * @param {object|null} input.pathData
 * @param {object} input.runMetrics              rsaRunMetricsRef.current
 * @param {object} input.selectedRouteCountByTask
 * @param {string} input.triggerSource
 * @param {object|null} input.stripingData  striping_data for a run with Striping Plan (stripingPlanData.js
 *                                           stripingDataForTasks); omitted when null
 * @param {object|null} input.runLabel        `run_label` (InternalComponents/runLabel.js buildRunLabel);
 *                                            sent on EVERY payload, null when the caller has none
 * @param {Date}   input.now
 */
export const buildRsaProcessPayload = ({
  projectId,
  projectName,
  projectType,
  userEmail = "",
  selectedTasks = [],
  selectedTaskNames = [],
  analysisTemplateIdMapping = {},
  hasRoutes = false,
  pathFolders = [],
  modelFolderIds = [],
  outputFolderId = "",
  sourceStorage = "drive",
  sourceFolderId = "",
  selectedFolderIds = [],
  wkt = "",
  horizontalData = null,
  embankmentData = null,
  pathData = null,
  runMetrics = {},
  selectedRouteCountByTask = {},
  triggerSource = DEFAULT_TRIGGER_SOURCE,
  runLabel = null,
  stripingData = null,
  now = new Date(),
}) => {
  const runLabelPayload =
    runLabel && typeof runLabel === "object" ? runLabel : null;
  const isRsaModelProject =
    String(projectType || "")
      .trim()
      .toLowerCase() === "rsa-model";
  const normalizedProjectTypeForPayload = isRsaModelProject
    ? "rsa-model"
    : "rsa-existing";
  const requestProjectName =
    String(projectName || "").trim() || `Project ${projectId}`;

  let analysisTemplateIds = templateIdsForTasks(
    selectedTaskNames,
    analysisTemplateIdMapping,
  );
  if (analysisTemplateIds.length === 0) {
    return {
      ok: false,
      code: "no_templates",
      message: "No valid analysis templates found for selected tasks.",
    };
  }

  let pathFoldersPayload = [];
  if (hasRoutes) {
    const aligned = alignTemplatesToPathFolders({
      selectedTaskNames,
      analysisTemplateIdMapping,
      pathFolders,
    });
    if (aligned.missingConfigTypes.length > 0) {
      return {
        ok: false,
        code: "missing_rsa_folders",
        missingConfigTypes: aligned.missingConfigTypes,
        message: `Missing RSA folder(s) for selected config type(s): ${aligned.missingConfigTypes.join(", ")}`,
      };
    }
    if (aligned.analysisTemplateIds.length === 0) {
      return {
        ok: false,
        code: "no_routes",
        message:
          "No analyses with routes to submit. Add route XML files to the RSA folders for the selected analyses, or deselect analyses that list 0 routes.",
      };
    }
    analysisTemplateIds = aligned.analysisTemplateIds;
    pathFoldersPayload = aligned.pathFolders;
  }

  const resolvedOutputFolderId = String(outputFolderId ?? "").trim();
  if (!resolvedOutputFolderId) {
    return {
      ok: false,
      code: "no_output_folder",
      message:
        "Deliverables folder google_drive_id was not found. Please set it in the project's MySQL row.",
    };
  }

  let folderIds = hasRoutes
    ? Array.from(
        new Set(
          [
            ...(sourceStorage === "s3" ? [] : modelFolderIds || []),
            ...pathFoldersPayload.map((item) => item?.path_folder_id),
          ]
            .filter((id) => id != null && String(id).trim() !== "")
            .map(String),
        ),
      )
    : (selectedFolderIds || [])
        .filter((id) => id != null && id !== "")
        .map(String);

  // Prepend Source folder ID so the Pipeline knows where LAS files live.
  // For S3-source projects, Pipeline discovers the bucket via SSM
  // (is_project_s3_source(project_id)), not via a folder ID — so we skip the
  // unshift to avoid sending the frontend's 's3:source' sentinel to the backend.
  if (!isRsaModelProject && sourceStorage !== "s3") {
    const sourceDriveId = normalizeId(sourceFolderId);
    if (sourceDriveId && !folderIds.includes(sourceDriveId)) {
      folderIds.unshift(sourceDriveId);
    }
  }

  if (folderIds.length === 0) {
    return {
      ok: false,
      code: "no_folders",
      message: "No valid folder IDs found for this project.",
    };
  }

  const selectedServicesForPayload = Array.from(
    new Set(
      selectedTaskNames
        .map((taskName) => TASK_OPTION_TO_SERVICE[taskName])
        .filter(Boolean),
    ),
  );
  const selectedRouteCountByService = selectedServicesForPayload.reduce(
    (acc, serviceName) => {
      acc[serviceName] = 0;
      return acc;
    },
    {},
  );
  for (const [taskName, routeCount] of Object.entries(
    selectedRouteCountByTask || {},
  )) {
    const serviceName = TASK_OPTION_TO_SERVICE[taskName];
    if (!serviceName || !selectedServicesForPayload.includes(serviceName)) {
      continue;
    }
    selectedRouteCountByService[serviceName] = routeCount;
  }

  const rawRunMetrics =
    runMetrics && typeof runMetrics === "object" ? runMetrics : {};
  // Billing figures leave out the services the form never bills (Striping Plan).
  const billedServicesForPayload = selectedServicesForPayload.filter(
    (serviceName) => !UNBILLED_SERVICES.has(serviceName),
  );
  const pickSelectedServiceMetrics = (
    sourceMap,
    services = selectedServicesForPayload,
  ) => {
    const out = {};
    for (const serviceName of services) {
      const num = Number(sourceMap[serviceName]);
      out[serviceName] = Number.isFinite(num) ? num : 0;
    }
    return out;
  };
  const mileageByService = pickSelectedServiceMetrics(
    safeMetricsMap(rawRunMetrics.mileage_by_service),
    billedServicesForPayload,
  );
  const maneuversByService = pickSelectedServiceMetrics(
    safeMetricsMap(rawRunMetrics.maneuvers_by_service),
  );
  const routeCountByService = pickSelectedServiceMetrics(
    safeMetricsMap(rawRunMetrics.route_count_by_service),
  );
  if (hasRoutes) {
    for (const serviceName of selectedServicesForPayload) {
      routeCountByService[serviceName] =
        Number(selectedRouteCountByService[serviceName]) || 0;
    }
  }

  const analysisTypeForPayload = deriveAnalysisType(selectedTasks);

  // Do NOT filter blank/synthetic ids here — the backend treats any blank or
  // s3: id as a signal to fall back to billing client metrics for the whole
  // run (owner decision, final review).
  const rawBillingFilesByService = safeMetricsMap(
    rawRunMetrics.billing_files_by_service,
  );
  const billingFilesByService = {};
  for (const serviceName of billedServicesForPayload) {
    const ids = rawBillingFilesByService[serviceName] || [];
    if (ids.length > 0) {
      billingFilesByService[serviceName] = [...new Set(ids)];
    }
  }
  const hasBillingFiles = Object.keys(billingFilesByService).length > 0;

  const triggeredAt = now.toISOString();
  const runLoggingPayload = {
    trigger_source: triggerSource,
    triggered_at: triggeredAt,
    selected_tasks: [...selectedTaskNames],
    selected_services: [...selectedServicesForPayload],
    submitted_analysis_template_ids: [...analysisTemplateIds],
    analysis_metrics: {
      mileage_by_service: mileageByService,
      maneuvers_by_service: maneuversByService,
      route_count_by_service: routeCountByService,
      mileage_total_miles: sumMetricValues(mileageByService),
      maneuvers_total: sumMetricValues(maneuversByService),
      route_total: sumMetricValues(routeCountByService),
      selected_project_id: projectId,
      metrics_generated_at: rawRunMetrics.metrics_generated_at || triggeredAt,
    },
  };

  const payload = hasRoutes
    ? {
        project_id: projectId,
        project_type: normalizedProjectTypeForPayload,
        projectType: normalizedProjectTypeForPayload,
        source_project_id: projectId,
        folder_ids: Array.from(new Set([...folderIds].filter(Boolean))),
        analysis_template_ids: analysisTemplateIds,
        ...(analysisTypeForPayload && {
          analysis_type: analysisTypeForPayload,
        }),
        horizontal_data: horizontalData || {},
        embankment_data: embankmentData || {},
        ...(stripingData && { striping_data: stripingData }),
        path_folders: pathFoldersPayload,
        output_folder_id: resolvedOutputFolderId,
        wkt: wkt || "",
        project_name: requestProjectName,
        user_email: userEmail,
        selected_task_names: [...selectedTaskNames],
        selected_services: [...selectedServicesForPayload],
        ...(hasBillingFiles && {
          billing_files_by_service: billingFilesByService,
        }),
        trigger_source: triggerSource,
        run_label: runLabelPayload,
        run_logging: runLoggingPayload,
      }
    : {
        project_id: projectId,
        project_type: normalizedProjectTypeForPayload,
        projectType: normalizedProjectTypeForPayload,
        source_project_id: projectId,
        project_name: requestProjectName,
        folder_ids: Array.from(new Set([...folderIds].filter(Boolean))),
        analysis_template_ids: analysisTemplateIds,
        ...(analysisTypeForPayload && {
          analysis_type: analysisTypeForPayload,
        }),
        horizontal_data: horizontalData || {},
        embankment_data: embankmentData || {},
        ...(stripingData && { striping_data: stripingData }),
        path_data: pathData ?? null,
        output_folder_id: resolvedOutputFolderId,
        user_email: userEmail,
        selected_task_names: [...selectedTaskNames],
        selected_services: [...selectedServicesForPayload],
        ...(hasBillingFiles && {
          billing_files_by_service: billingFilesByService,
        }),
        trigger_source: triggerSource,
        run_label: runLabelPayload,
        run_logging: runLoggingPayload,
      };

  return { ok: true, payload, projectName: requestProjectName, selectedServices: selectedServicesForPayload };
};
